"use client";

import { useRef } from "react";
import { motion } from "framer-motion";
import { Sparkles, ShieldCheck, House as Home, Calendar, ArrowRight } from 'lucide-react';
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useGSAP } from "@gsap/react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Link } from "@/components/common/Link";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
gsap.registerPlugin(useGSAP, ScrollTrigger);
const services = [{
  id: "standard",
  eyebrow: "TIER 01",
  heading: "STANDARD / MAINTENANCE CLEANING",
  summary: "Routine upkeep designed to keep your home consistently fresh, polished, and immaculate week after week.",
  subgrid: [{
    title: "Kitchen & Dining",
    details: "Countertops thoroughly wiped and sanitized, exterior of all appliances polished (refrigerator, dishwasher, microwave), sink and faucets scrubbed, kitchen table wiped down, floors vacuumed and mopped."
  }, {
    title: "Bathrooms",
    details: "Showers and bathtubs washed and disinfected, toilets thoroughly sanitized inside and out, mirrors polished streak-free, chrome fixtures shined, vanities and countertops wiped, floors cleaned and sanitized."
  }, {
    title: "Living Areas & Bedrooms",
    details: "Accessible surfaces dusted and polished, furniture wiped down, mirrors cleaned, beds neatly made (upon request), cobwebs removed, trash emptied, floors thoroughly vacuumed and mopped throughout."
  }],
  icon: Home
}, {
  id: "deep",
  eyebrow: "TIER 02",
  heading: "DEEP CLEANING",
  summary: "A rigorous, top-to-bottom comprehensive service that reaches every neglected corner and hidden surface.",
  subgrid: [{
    title: "Kitchen (Detailed)",
    details: "Interior microwave cleaning, detailed grease removal on backsplash and range hood, thorough spot-cleaning of cabinet exteriors, baseboards wiped, and meticulous sanitization of all food prep zones."
  }, {
    title: "Bathrooms (Detailed)",
    details: "Intensive grout scrubbing, hard water stain removal on glass shower doors and fixtures, doors and doorframes hand-wiped, high-dusting of vents, light fixtures, and corners."
  }, {
    title: "Living Areas (Detailed)",
    details: "Hand-wiping all baseboards, window sills, door frames, ceiling fan blades, picture frames, and detailed dusting of blinds and intricate furniture carvings."
  }],
  icon: Sparkles
}, {
  id: "move",
  eyebrow: "TIER 03",
  heading: "MOVE-IN / MOVE-OUT CLEANING",
  summary: "Immaculate turnover service ensuring properties are completely pristine for new occupants or landlords.",
  subgrid: [{
    title: "Kitchen (Turnover)",
    details: "Inside and outside of all empty cabinets and drawers completely wiped out, oven interior degreased and cleaned, refrigerator interior sanitized, dishwasher interior wiped, pantry shelves dusted."
  }, {
    title: "Bathrooms (Turnover)",
    details: "Sanitization of all drawers and medicine cabinets inside and out, exhaustive disinfecting of tubs, showers, tiles, toilets, and fixtures, spotless glass and mirror polishing."
  }, {
    title: "Living Areas (Turnover)",
    details: "Comprehensive wall spot cleaning, closet interiors and shelving wiped down, baseboards, doors, and doorframes washed, floors deep-vacuumed and sanitized throughout."
  }],
  icon: ShieldCheck
}, {
  id: "shortterm",
  eyebrow: "TIER 04",
  heading: "SHORT-TERM RENTAL TURNOVER",
  summary: "Rapid, reliable turn-key cleaning and staging prep tailored for Airbnb, VRBO, and vacation rental hosts.",
  subgrid: [{
    title: "Kitchen & Reset",
    details: "Complete restock readiness check, appliance inspection, interior microwave cleaning, coffee station reset, countertop disinfection, and prompt trash removal and bin lining."
  }, {
    title: "Bathrooms & Linens",
    details: "Immaculate towel folding and staging, pristine surface disinfection, mirror streak-free shine, toiletry amenity arrangement, and seamless bathroom turnover."
  }, {
    title: "Living & Staging",
    details: "Rapid turnaround vacuuming, perfect pillow, blanket, and throw staging, remote control placement, surface dust removal, and final aesthetic quality check for guest arrival."
  }],
  icon: Calendar
}];
const containerVariants = {
  hidden: {
    opacity: 0
  },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 20
  },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: "easeOut"
    }
  }
};
export default function Services() {
  const container = useRef<HTMLDivElement>(null);
  return <section data-section-id="24706" id="servicetiersaccordion" data-nav="light" className="bg-background py-28 md:py-36 overflow-hidden">
      <div ref={container} className="mx-auto max-w-[1360px] px-5 md:px-6 lg:px-10">
        <motion.div initial={{
        opacity: 0,
        y: -20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="text-center mb-16 md:mb-20 max-w-3xl mx-auto">
          <Badge variant="secondary" className="mb-4 bg-secondary/10 text-secondary hover:bg-secondary/20 px-3 py-1">
            <Home className="w-3.5 h-3.5 mr-1.5" style={{
            strokeWidth: 1.5
          }} />
            RESIDENTIAL SERVICE TIERS
          </Badge>
          <h2 className="font-serif font-bold uppercase text-foreground" style={{
          fontSize: "clamp(2rem, 4.5vw, 4rem)",
          lineHeight: "1.05",
          letterSpacing: "-0.01em"
        }}>
            CUSTOMIZED CLEANING PACKAGES FOR EVERY HOME
          </h2>
          <p className="mt-4 font-sans text-lg" style={{
          color: "var(--muted-foreground)"
        }}>
            Explore our professional residential cleaning tiers. Each service is delivered by our network of vetted independent cleaning professionals with transparent standards.
          </p>
        </motion.div>

        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true
      }} className="max-w-5xl mx-auto">
          <Accordion type="single" collapsible defaultValue="standard" className="w-full space-y-4">
            {services.map((service, i) => {
            const IconComponent = service.icon;
            return <motion.div key={service.id} variants={itemVariants} data-index={i}>
                  <AccordionItem value={service.id} className="border border-border rounded-xl bg-card px-6 py-2 shadow-sm transition-all duration-300 data-[state=open]:border-primary data-[state=open]:shadow-md">
                    <AccordionTrigger className="hover:no-underline py-6 [&[data-state=open]>div>div>div]:text-primary">
                      <div className="flex items-center gap-5 text-left w-full pr-4">
                        <div className="p-3 rounded-lg flex items-center justify-center flex-shrink-0" style={{
                      backgroundColor: "var(--accent)",
                      color: "var(--accent-foreground)"
                    }}>
                          <IconComponent className="h-6 w-6" style={{
                        strokeWidth: 1.5
                      }} />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-1">
                            <span className="font-sans text-xs font-bold uppercase tracking-widest px-2 py-0.5 rounded bg-primary/10" style={{
                          color: "var(--chart-2)"
                        }}>
                              {service.eyebrow}
                            </span>
                          </div>
                          <h3 className="font-serif font-bold uppercase text-foreground" style={{
                        fontSize: "clamp(1.25rem, 2.5vw, 1.875rem)",
                        lineHeight: "1.2"
                      }}>
                            {service.heading}
                          </h3>
                          <p className="mt-2 font-sans text-sm md:text-base text-muted-foreground font-medium max-w-3xl">
                            {service.summary}
                          </p>
                        </div>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="px-2 pb-8 pt-4 border-t border-border">
                        <div className="mb-6">
                          <h4 className="font-sans text-xs font-bold uppercase tracking-widest text-muted-foreground mb-4">
                            SERVICE CHECKLIST &amp; AREAS COVERED
                          </h4>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                          {service.subgrid.map((col, colIndex) => <div key={colIndex} data-index={colIndex} className="p-6 rounded-lg bg-background border border-border shadow-2xs flex flex-col justify-between">
                              <div>
                                <h5 className="font-serif font-bold text-base uppercase mb-2" style={{
                            color: "var(--primary)"
                          }}>
                                  {col.title}
                                </h5>
                                <p className="font-sans text-sm font-semibold leading-relaxed" style={{
                            color: "var(--foreground)"
                          }}>
                                  {col.details}
                                </p>
                              </div>
                            </div>)}
                        </div>

                        <div className="mt-8 flex items-center justify-between pt-6 border-t border-border flex-wrap gap-4">
                          <div className="flex items-center gap-2">
                            <span className="inline-block size-2 rounded-lg" style={{
                          backgroundColor: "var(--chart-2)"
                        }} />
                            <span className="font-sans text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                              Insured &amp; Bonded Network Professional
                            </span>
                          </div>
                          <Button asChild variant="default" className="rounded-lg px-6 uppercase font-bold text-xs tracking-wider">
                            <Link to="/GetAQuotePage" className="inline-flex items-center gap-2">
                              Request This Tier
                              <ArrowRight className="h-4 w-4" style={{
                            strokeWidth: 1.5
                          }} />
                            </Link>
                          </Button>
                        </div>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                </motion.div>;
          })}
          </Accordion>
        </motion.div>
      </div>
    </section>;
}